import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ArchivePlanApplicationErrors,
  ArchivePlanResponse,
  ClearPrimaryApplicationErrors,
  CreatePlanApplicationErrors,
  CreatePlanValidationErrors,
  GetPlanApplicationErrors,
  GetPlanStatsApplicationErrors,
  GetPlanStatsResponse,
  ListPlansApplicationErrors,
  ListPlansOptions,
  ListPlansResponse,
  ListPlansValidationErrors,
  ListPublicPlansApplicationErrors,
  ListPublicPlansOptions,
  ListPublicPlansResponse,
  MakePlanPrimaryApplicationErrors,
  MakePlanPrimaryResponse,
  Plan,
  PlanArchivedEnvelope,
  PlanBuyerCanCancelUpdatedEnvelope,
  PlanCreatedEnvelope,
  PlanQuery,
  PlanUpdatedEnvelope,
  PlansQueryBuilder,
  QueryPublicPlansApplicationErrors,
  QueryPublicPlansResponse,
  QueryPublicPlansValidationErrors,
  SetPlanVisibilityApplicationErrors,
  SetPlanVisibilityResponse,
  UpdatePlan,
  UpdatePlanApplicationErrors,
  UpdatePlanValidationErrors,
  archivePlan as universalArchivePlan,
  clearPrimary as universalClearPrimary,
  createPlan as universalCreatePlan,
  getPlan as universalGetPlan,
  getPlanStats as universalGetPlanStats,
  listPlans as universalListPlans,
  listPublicPlans as universalListPublicPlans,
  makePlanPrimary as universalMakePlanPrimary,
  queryPublicPlans as universalQueryPublicPlans,
  setPlanVisibility as universalSetPlanVisibility,
  typedQueryPublicPlans as universalTypedQueryPublicPlans,
  updatePlan as universalUpdatePlan,
} from './pricing-plans-v2-plan-plans.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/pricing-plans' };

export function listPublicPlans(
  httpClient: HttpClient
): ListPublicPlansSignature {
  return (options?: ListPublicPlansOptions) =>
    universalListPublicPlans(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListPublicPlansSignature {
  /**
   * Retrieves a list of up to 100 public pricing plans. Public plans are visible plans that are available to site visitors and can be purchased.
   * @param - Options for filtering and paging the list of public plans.
   * @deprecated
   */
  (options?: ListPublicPlansOptions): Promise<
    NonNullablePaths<
      ListPublicPlansResponse,
      | `plans`
      | `plans.${number}._id`
      | `plans.${number}.pricing.singlePaymentForDuration.unit`
      | `plans.${number}.pricing.price.value`
      | `plans.${number}.pricing.price.currency`
      | `plans.${number}.primary`,
      6
    > & {
      __applicationErrorsType?: ListPublicPlansApplicationErrors;
    }
  >;
}

export function queryPublicPlans(
  httpClient: HttpClient
): QueryPublicPlansSignature {
  return () =>
    universalQueryPublicPlans(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPublicPlansSignature {
  /**
   * Creates a query to retrieve a list of public pricing plans.
   *
   *
   * The `queryPublicPlans()` method builds a query to retrieve a list of up to 1,000 public plans and returns a [`PublicPlansQueryBuilder`](#plansquerybuilder) object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](#plansquerybuilder/find) method.
   *
   * You can refine the query by chaining `PublicPlansQueryBuilder` methods onto the query. `PublicPlansQueryBuilder` methods enable you to sort, filter, and control the results that `queryPublicPlans()` returns.
   *
   * `queryPublicPlans()` runs with the following `PublicPlansQueryBuilder` defaults that you can override:
   * - [`skip`](#plansquerybuilder/skip): `0`
   * - [`limit`](#plansquerybuilder/limit): `50`
   *
   * The methods that are chained to `queryPublicPlans()` are applied in the order they are called. For example, if you sort on the `_createdDate` property in ascending order and then on the `_id` property in ascending order, the results are sorted first by the created date of the items and then, if there are multiple results with the same date, the items are sorted by `_id` in ascending order, per created date value.
   *
   * The following `PublicPlansQueryBuilder` methods are supported for the `queryPublicPlans()` method. For a full description of the Plans object, see the object returned for the [`items`](#plansqueryresult/items) property in [`PublicPlansQueryResult`](#plansqueryresult).
   * @deprecated
   */
  (): PlansQueryBuilder;
}

export function typedQueryPublicPlans(
  httpClient: HttpClient
): TypedQueryPublicPlansSignature {
  return (query: PlanQuery) =>
    universalTypedQueryPublicPlans(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPublicPlansSignature {
  /** @returns Fulfilled - The query definition used to run the query using the [`find()`](#find) method. */
  (query: PlanQuery): Promise<
    NonNullablePaths<
      QueryPublicPlansResponse,
      | `plans`
      | `plans.${number}._id`
      | `plans.${number}.pricing.singlePaymentForDuration.unit`
      | `plans.${number}.pricing.price.value`
      | `plans.${number}.pricing.price.currency`
      | `plans.${number}.primary`,
      6
    > & {
      __applicationErrorsType?: QueryPublicPlansApplicationErrors;
      __validationErrorsType?: QueryPublicPlansValidationErrors;
    }
  >;
}

export function getPlan(httpClient: HttpClient): GetPlanSignature {
  return (_id: string) =>
    universalGetPlan(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPlanSignature {
  /**
   * Retrieves a pricing plan by ID.
   * @param - Plan ID.
   * @returns Pricing plan.
   * @deprecated
   */
  (_id: string): Promise<
    NonNullablePaths<
      Plan,
      | `_id`
      | `perks.values`
      | `pricing.subscription.cycleDuration.unit`
      | `pricing.price.value`
      | `pricing.price.currency`
      | `archived`
      | `primary`
      | `hasOrders`,
      5
    > & {
      __applicationErrorsType?: GetPlanApplicationErrors;
    }
  >;
}

export function listPlans(httpClient: HttpClient): ListPlansSignature {
  return (options?: ListPlansOptions) =>
    universalListPlans(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListPlansSignature {
  /**
   * Retrieves a list of up to 100 pricing plans (including public, hidden, and archived plans).
   * @param - Options for filtering and paging the list of plans.
   * @deprecated
   */
  (options?: ListPlansOptions): Promise<
    NonNullablePaths<
      ListPlansResponse,
      | `plans`
      | `plans.${number}._id`
      | `plans.${number}.pricing.singlePaymentForDuration.unit`
      | `plans.${number}.pricing.price.value`
      | `plans.${number}.pricing.price.currency`
      | `plans.${number}.archived`
      | `plans.${number}.primary`
      | `plans.${number}.hasOrders`,
      6
    > & {
      __applicationErrorsType?: ListPlansApplicationErrors;
      __validationErrorsType?: ListPlansValidationErrors;
    }
  >;
}

export function getPlanStats(httpClient: HttpClient): GetPlanStatsSignature {
  return () =>
    universalGetPlanStats(
      // @ts-ignore
      { httpClient }
    );
}

interface GetPlanStatsSignature {
  /**
   * Gets statistics about the pricing plans. Currently provides only the total number of pricing plans, including archived plans.
   * @deprecated
   */
  (): Promise<
    NonNullablePaths<GetPlanStatsResponse, `totalPlans`, 2> & {
      __applicationErrorsType?: GetPlanStatsApplicationErrors;
    }
  >;
}

export function createPlan(httpClient: HttpClient): CreatePlanSignature {
  return (
    plan: NonNullablePaths<
      Plan,
      | `name`
      | `pricing`
      | `pricing.singlePaymentForDuration.count`
      | `pricing.subscription.cycleCount`
      | `pricing.subscription.cycleDuration`
      | `pricing.subscription.cycleDuration.count`,
      5
    >
  ) =>
    universalCreatePlan(
      plan,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePlanSignature {
  /**
   * Creates a pricing plan. The specified `plan` object must contain a pricing model. A pricing model can be 1 of the following:
   * - **A subscription**: A subscription with recurring payments and how often the plan occurs. Subscriptions can have free trial days.
   * - **A plan that doesn't renew**: A single payment for a specific duration that doesn't renew.
   * - **An unlimited plan**: A single payment for an unlimited amount of time (until canceled).
   * Pricing plans are available to the Wix user in the Pricing Plans section in a site's dashboard.
   * @param - Information for the plan being created.
   * @returns Created plan.
   * @deprecated
   */
  (
    plan: NonNullablePaths<
      Plan,
      | `name`
      | `pricing`
      | `pricing.singlePaymentForDuration.count`
      | `pricing.subscription.cycleCount`
      | `pricing.subscription.cycleDuration`
      | `pricing.subscription.cycleDuration.count`,
      5
    >
  ): Promise<
    NonNullablePaths<
      Plan,
      | `_id`
      | `perks.values`
      | `pricing.subscription.cycleDuration.unit`
      | `pricing.price.value`
      | `pricing.price.currency`
      | `archived`
      | `primary`
      | `hasOrders`,
      5
    > & {
      __applicationErrorsType?: CreatePlanApplicationErrors;
      __validationErrorsType?: CreatePlanValidationErrors;
    }
  >;
}

export function updatePlan(httpClient: HttpClient): UpdatePlanSignature {
  return (_id: string, plan: UpdatePlan) =>
    universalUpdatePlan(
      _id,
      plan,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePlanSignature {
  /**
   * Updates a pricing plan.
   * Updating a plan doesn't impact existing orders made for the plan. All orders keep the details of the original plan that was active at the time of purchase.
   * @param - ID of the plan to update.
   * @param - Options for updating the plan.
   * @returns Updated plan.
   * @deprecated
   */
  (_id: string, plan: UpdatePlan): Promise<
    NonNullablePaths<
      Plan,
      | `_id`
      | `perks.values`
      | `pricing.subscription.cycleDuration.unit`
      | `pricing.price.value`
      | `pricing.price.currency`
      | `archived`
      | `primary`
      | `hasOrders`,
      5
    > & {
      __applicationErrorsType?: UpdatePlanApplicationErrors;
      __validationErrorsType?: UpdatePlanValidationErrors;
    }
  >;
}

export function setPlanVisibility(
  httpClient: HttpClient
): SetPlanVisibilitySignature {
  return (_id: string, visible: boolean) =>
    universalSetPlanVisibility(
      _id,
      visible,
      // @ts-ignore
      { httpClient }
    );
}

interface SetPlanVisibilitySignature {
  /**
   * Sets visibility for pricing plans. Visible plans are considered public plans.
   * By default, pricing plans are public, meaning they are visible. Plans can be hidden so that site members and visitors can't choose them.
   * As opposed to archiving, setting visibility can be reversed. This means that a public plan can be hidden, and a hidden plan can be made public (visible). (An archived plan always remains archived and can't be made active again.)
   * Changing a plan’s visibility doesn't impact existing orders for the plan. All orders for hidden plans are still active and keep their perks.
   * @param - Whether a plan is visible to members and site visitors. Updates the `public` field.
   * @param - The ID of the plan to either display or hide on the site page.
   * @param - Plan visibility options.
   * @deprecated
   */
  (_id: string, visible: boolean): Promise<
    NonNullablePaths<
      SetPlanVisibilityResponse,
      | `plan._id`
      | `plan.perks.values`
      | `plan.pricing.subscription.cycleDuration.unit`
      | `plan.pricing.price.value`
      | `plan.pricing.price.currency`
      | `plan.archived`
      | `plan.primary`
      | `plan.hasOrders`,
      6
    > & {
      __applicationErrorsType?: SetPlanVisibilityApplicationErrors;
    }
  >;
}

export function makePlanPrimary(
  httpClient: HttpClient
): MakePlanPrimarySignature {
  return (_id: string) =>
    universalMakePlanPrimary(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface MakePlanPrimarySignature {
  /**
   * Marks a pricing plan as the primary pricing plan. When viewing pricing plans on a site, the primary plan is highlighted with a customizable ribbon. Only a single plan can be marked as a primary plan at any given time. If there is an existing plan marked as primary, calling Make Plan Primary causes the existing primary plan to lose its primary status.
   * @param - ID of the pricing plan to set as the primary plan.
   * @deprecated
   */
  (_id: string): Promise<
    NonNullablePaths<
      MakePlanPrimaryResponse,
      | `plan._id`
      | `plan.perks.values`
      | `plan.pricing.subscription.cycleDuration.unit`
      | `plan.pricing.price.value`
      | `plan.pricing.price.currency`
      | `plan.archived`
      | `plan.primary`
      | `plan.hasOrders`,
      6
    > & {
      __applicationErrorsType?: MakePlanPrimaryApplicationErrors;
    }
  >;
}

export function clearPrimary(httpClient: HttpClient): ClearPrimarySignature {
  return () =>
    universalClearPrimary(
      // @ts-ignore
      { httpClient }
    );
}

interface ClearPrimarySignature {
  /**
   * Sets all pricing plans as not primary. When viewing pricing plans on a site, no plan is highlighted with a customizable ribbon.
   * @deprecated
   */
  (): Promise<
    void & {
      __applicationErrorsType?: ClearPrimaryApplicationErrors;
    }
  >;
}

export function archivePlan(httpClient: HttpClient): ArchivePlanSignature {
  return (_id: string) =>
    universalArchivePlan(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchivePlanSignature {
  /**
   * Archives a single plan.
   * When a plan is archived, it's no longer visible as a public plan that can be chosen by site members or visitors.
   * This is because the plan's `public` property is automatically set to `false`.
   * Archived plans can't be purchased or reactivated.
   * Plan archiving doesn't impact existing orders made for the plan. All orders for the plan are still active and keep their perks, payment options, and terms.
   * Wix users can see archived plans in a site's dashboard under **Pricing Plans -> Archived Plans**.
   * > **Note:** An attempt to archive an already-archived plan throws an error.
   * @param - ID of the active plan to archive.
   * @deprecated
   */
  (_id: string): Promise<
    NonNullablePaths<
      ArchivePlanResponse,
      | `plan._id`
      | `plan.perks.values`
      | `plan.pricing.subscription.cycleDuration.unit`
      | `plan.pricing.price.value`
      | `plan.pricing.price.currency`
      | `plan.archived`
      | `plan.primary`
      | `plan.hasOrders`,
      6
    > & {
      __applicationErrorsType?: ArchivePlanApplicationErrors;
    }
  >;
}

export const onPlanBuyerCanCancelUpdated = EventDefinition(
  'wix.pricing_plans.plan_buyer_can_cancel_updated',
  true,
  (event: PlanBuyerCanCancelUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.plan.createdDate' },
            { path: 'data.plan.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PlanBuyerCanCancelUpdatedEnvelope>();
export const onPlanCreated = EventDefinition(
  'wix.pricing_plans.plan_created',
  true,
  (event: PlanCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PlanCreatedEnvelope>();
export const onPlanArchived = EventDefinition(
  'wix.pricing_plans.plan_plan_archived',
  true,
  (event: PlanArchivedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.plan.createdDate' },
            { path: 'data.plan.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PlanArchivedEnvelope>();
export const onPlanUpdated = EventDefinition(
  'wix.pricing_plans.plan_updated',
  true,
  (event: PlanUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PlanUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  AppliedAt,
  ArchivePlanRequest,
  ArchivePlanResponse,
  ArchivedFilter,
  ArrangePlansRequest,
  ArrangePlansResponse,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkArchivePlanRequest,
  BulkArchivePlanResponse,
  BulkPlanResult,
  BuyerCanCancelUpdated,
  ClearPrimaryRequest,
  ClearPrimaryResponse,
  CountPlansRequest,
  CountPlansResponse,
  CreatePlanRequest,
  CreatePlanResponse,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  Duration,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Fee,
  FeeConfig,
  GetPlanRequest,
  GetPlanResponse,
  GetPlanStatsRequest,
  GetPlanStatsResponse,
  GetPlansPremiumStatusRequest,
  GetPlansPremiumStatusResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListPlansOptions,
  ListPlansRequest,
  ListPlansResponse,
  ListPublicPlansOptions,
  ListPublicPlansRequest,
  ListPublicPlansResponse,
  MakePlanPrimaryRequest,
  MakePlanPrimaryResponse,
  MessageEnvelope,
  Money,
  Paging,
  PagingMetadataV2,
  PeriodUnit,
  Plan,
  PlanArchived,
  PlanArchivedEnvelope,
  PlanBuyerCanCancelUpdatedEnvelope,
  PlanCreatedEnvelope,
  PlanQuerySpec,
  PlanUpdatedEnvelope,
  PlansQueryBuilder,
  PlansQueryResult,
  Pricing,
  PricingPricingModelOneOf,
  PublicFilter,
  PublicPlan,
  QueryPublicPlansRequest,
  QueryPublicPlansResponse,
  QueryV2,
  Recurrence,
  RestoreInfo,
  SearchPlansRequest,
  SearchPlansResponse,
  SetPlanVisibilityRequest,
  SetPlanVisibilityResponse,
  SortOrder,
  Sorting,
  StringList,
  UpdatePlan,
  UpdatePlanRequest,
  UpdatePlanResponse,
  WebhookIdentityType,
  utils,
} from './pricing-plans-v2-plan-plans.universal.js';
